import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Ethereum wallet state
 */
export interface EthWalletState {
  isConnected: boolean;
  isConnecting: boolean;
  address: string | null;
  chainId: number | null;
  balance: string | null;
  error: string | null;
}

/**
 * Ethereum wallet actions and state
 */
export interface UseEthWalletResult extends EthWalletState {
  connect: () => Promise<void>;
  disconnect: () => void;
  switchToMainnet: () => Promise<void>;
  isMetaMaskInstalled: boolean;
}

// Ethereum Mainnet chain ID
const ETH_MAINNET_CHAIN_ID = 1;

/**
 * Check if MetaMask is installed
 */
function isMetaMaskInstalled(): boolean {
  return typeof window !== 'undefined' && 
         typeof window.ethereum !== 'undefined' && 
         window.ethereum.isMetaMask === true;
}

/**
 * Get the ethereum provider
 */
function getProvider(): EthereumProvider | null {
  if (typeof window !== 'undefined' && window.ethereum) {
    return window.ethereum;
  }
  return null;
}

/**
 * Hook for Ethereum wallet connection (MetaMask)
 * 
 * This provides a simple interface for connecting to MetaMask and
 * managing Ethereum wallet state for bridge operations.
 */
export function useEthWallet(): UseEthWalletResult {
  const [state, setState] = useState<EthWalletState>({
    isConnected: false,
    isConnecting: false,
    address: null,
    chainId: null,
    balance: null,
    error: null,
  });

  const metamaskInstalled = isMetaMaskInstalled();
  
  // Use ref for fetchBalance to avoid dependency issues
  const fetchBalanceRef = useRef<(address: string) => Promise<void>>();

  // Define fetchBalance
  const fetchBalance = useCallback(async (address: string): Promise<void> => {
    const provider = getProvider();
    if (!provider) return;

    try {
      const balanceHex = await provider.request({
        method: 'eth_getBalance',
        params: [address, 'latest'],
      }) as string;
      
      // Convert from wei to ETH
      const balanceWei = BigInt(balanceHex);
      const balanceEth = Number(balanceWei) / 1e18;
      
      setState(prev => ({
        ...prev,
        balance: balanceEth.toFixed(4),
      }));
    } catch (err) {
      console.error('Error fetching ETH balance:', err);
    }
  }, []);

  fetchBalanceRef.current = fetchBalance;

  // Check existing connection on mount
  useEffect(() => {
    const checkConnection = async (): Promise<void> => {
      const provider = getProvider();
      if (!provider) return;

      try {
        // Check if already connected
        const accounts = await provider.request({ method: 'eth_accounts' }) as string[];
        if (accounts.length > 0 && accounts[0] !== undefined) {
          const chainIdHex = await provider.request({ method: 'eth_chainId' }) as string;
          const chainId = parseInt(chainIdHex, 16);
          const address = accounts[0];
          
          setState(prev => ({
            ...prev,
            isConnected: true,
            address,
            chainId,
          }));

          // Fetch balance
          await fetchBalanceRef.current?.(address);
        }
      } catch (err) {
        console.error('Error checking Ethereum connection:', err);
      }
    };

    void checkConnection();
  }, []);

  // Set up event listeners
  useEffect(() => {
    const provider = getProvider();
    if (!provider) return;

    const handleAccountsChanged = (accounts: unknown): void => {
      const accts = accounts as string[];
      if (accts.length === 0) {
        // User disconnected
        setState({
          isConnected: false,
          isConnecting: false,
          address: null,
          chainId: null,
          balance: null,
          error: null,
        });
      } else {
        const address = accts[0] ?? null;
        setState(prev => ({
          ...prev,
          address,
        }));
        if (address !== null) {
          void fetchBalanceRef.current?.(address);
        }
      }
    };

    const handleChainChanged = (chainIdHex: unknown): void => {
      const chainId = parseInt(chainIdHex as string, 16);
      setState(prev => ({
        ...prev,
        chainId,
      }));
    };

    const handleDisconnect = (): void => {
      setState({
        isConnected: false,
        isConnecting: false,
        address: null,
        chainId: null,
        balance: null,
        error: null,
      });
    };

    provider.on('accountsChanged', handleAccountsChanged);
    provider.on('chainChanged', handleChainChanged);
    provider.on('disconnect', handleDisconnect);

    return (): void => {
      provider.removeListener('accountsChanged', handleAccountsChanged);
      provider.removeListener('chainChanged', handleChainChanged);
      provider.removeListener('disconnect', handleDisconnect);
    };
  }, []);

  const connect = useCallback(async (): Promise<void> => {
    const provider = getProvider();
    if (!provider) {
      setState(prev => ({
        ...prev,
        error: 'MetaMask is not installed',
      }));
      return;
    }

    setState(prev => ({ ...prev, isConnecting: true, error: null }));

    try {
      // Request account access
      const accounts = await provider.request({
        method: 'eth_requestAccounts',
      }) as string[];

      if (accounts.length === 0 || accounts[0] === undefined) {
        throw new Error('No accounts returned');
      }

      // Get chain ID
      const chainIdHex = await provider.request({ method: 'eth_chainId' }) as string;
      const chainId = parseInt(chainIdHex, 16);
      const address = accounts[0];

      setState(prev => ({
        ...prev,
        isConnected: true,
        isConnecting: false,
        address,
        chainId,
      }));

      // Fetch balance
      await fetchBalance(address);
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Failed to connect';
      setState(prev => ({
        ...prev,
        isConnecting: false,
        error,
      }));
    }
  }, [fetchBalance]);

  const disconnect = useCallback((): void => {
    // Note: MetaMask doesn't support programmatic disconnect
    // We just clear our local state
    setState({
      isConnected: false,
      isConnecting: false,
      address: null,
      chainId: null,
      balance: null,
      error: null,
    });
  }, []);

  const switchToMainnet = useCallback(async (): Promise<void> => {
    const provider = getProvider();
    if (!provider) return;

    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${ETH_MAINNET_CHAIN_ID.toString(16)}` }],
      });
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Failed to switch network';
      setState(prev => ({ ...prev, error }));
    }
  }, []);

  return {
    ...state,
    connect,
    disconnect,
    switchToMainnet,
    isMetaMaskInstalled: metamaskInstalled,
  };
}

/**
 * Ethereum provider interface (window.ethereum)
 */
interface EthereumProvider {
  isMetaMask?: boolean;
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  on: (event: string, handler: (...args: unknown[]) => void) => void;
  removeListener: (event: string, handler: (...args: unknown[]) => void) => void;
}

// Extend Window interface
declare global {
  interface Window {
    ethereum?: EthereumProvider;
  }
}
